import React, { useEffect, useRef, useState } from 'react';
import { Film } from 'lucide-react';
import { aiInterviewsAPI, fmtClock, SEGMENT_LABELS } from '../../../lib/aiInterviewApi';
import { Section } from '../recruiter/common';

const FLAG = { manipulation_attempt: 'Tried to instruct the AI', protected_info_excluded: 'Protected info excluded from scoring' };

function nearestTurn(turns, target) {
  if (target.turn != null) return turns.find((t) => t.seq === target.turn) || null;
  if (target.start_ms == null) return null;
  let best = null;
  turns.forEach((t) => { if ((t.start_ms ?? 0) <= target.start_ms) best = t; });
  return best || turns[0] || null;
}

const Turn = ({ t, active, onClick }) => (
  <div id={`turn-${t.seq}`} className={`iv-turn ${t.speaker === 'candidate' ? 'is-cand' : ''} ${active ? 'is-active' : ''}`} onClick={onClick}
    role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && onClick()} data-testid={`transcript-turn-${t.seq}`}>
    <time>{fmtClock(t.start_ms)}</time>
    <div>
      <b>{t.speaker === 'ai' ? 'AI interviewer' : 'Candidate'}</b>
      {t.typed && <span className="iv-pill iv-tone-slate" style={{ marginLeft: 6 }}>typed</span>}
      {(t.flags || []).map((f) => <span key={f} className="iv-pill iv-tone-amber" style={{ marginLeft: 6 }}>{FLAG[f] || f}</span>)}
      <p>{t.text}</p>
    </div>
  </div>
);

const DEFAULT_LOADERS = { parts: (id) => aiInterviewsAPI.recordingParts(id), part: (id, p) => aiInterviewsAPI.recording(id, p) };

export default function RecordingTranscript({ id, turns, seekRef, loaders = DEFAULT_LOADERS, recording = true }) {
  const [parts, setParts] = useState(null);
  const [part, setPart] = useState(null);
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(null);
  const video = useRef(null);
  const urls = useRef({});
  const pending = useRef(null);

  useEffect(() => {
    if (!recording) { setParts([]); return undefined; }
    loaders.parts(id).then(({ data }) => { setParts(data); if (data.length) setPart(data[0].part); }).catch(() => setParts([]));
    const cache = urls.current;
    return () => Object.values(cache).forEach((u) => URL.revokeObjectURL(u));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPart = async (p) => {
    if (!urls.current[p]) {
      setLoading(true);
      try {
        const { data } = await loaders.part(id, p);
        urls.current[p] = URL.createObjectURL(data);
      } finally {
        setLoading(false);
      }
    }
    setPart(p);
    setUrl(urls.current[p]);
  };

  const applyPending = (v) => {
    if (pending.current == null) return;
    v.currentTime = pending.current;
    pending.current = null;
    v.play().catch(() => {});
  };
  const onMeta = (e) => {
    const v = e.target;
    if (v.duration === Infinity || Number.isNaN(v.duration)) {
      const fix = () => { v.removeEventListener('durationchange', fix); v.currentTime = 0; applyPending(v); };
      v.addEventListener('durationchange', fix);
      v.currentTime = 1e101;
      return;
    }
    applyPending(v);
  };
  const onTime = (e) => {
    const ms = e.target.currentTime * 1000;
    let cur = null;
    turns.forEach((t) => { if (t.rec_part === part && t.rec_offset_ms != null && t.rec_offset_ms <= ms) cur = t.seq; });
    if (cur !== null && cur !== active) setActive(cur);
  };

  seekRef.current = async (target) => {
    const t = nearestTurn(turns, target);
    if (t) {
      setActive(t.seq);
      document.getElementById(`turn-${t.seq}`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    const p = target.rec_part ?? t?.rec_part;
    let off = target.rec_offset_ms ?? null;
    if (off == null && t?.rec_offset_ms != null) off = t.rec_offset_ms + Math.max(0, (target.start_ms ?? t.start_ms ?? 0) - (t.start_ms ?? 0));
    if (p == null || off == null || !parts?.some((x) => x.part === p)) return;
    pending.current = off / 1000;
    if (urls.current[p] && part === p && video.current?.readyState >= 1) applyPending(video.current);
    else await loadPart(p);
  };

  let lastSeg = null;
  return (
    <Section title={recording ? "Recording & transcript" : "Transcript"} testId="recording-transcript">
      {!recording ? null : parts === null ? <p className="iv-small">Checking for a recording…</p> : !parts.length ? (
        <div className="iv-note" data-testid="recording-none">No recording is available for this interview (it may still be uploading, or was deleted under the retention policy).</div>
      ) : (
        <div style={{ marginBottom: '0.9rem' }}>
          {url ? (
            <video ref={video} src={url} controls playsInline onLoadedMetadata={onMeta} onTimeUpdate={onTime}
              style={{ width: '100%', borderRadius: 12, background: '#0B1A20', aspectRatio: '4 / 3' }} data-testid="recording-player" />
          ) : (
            <button type="button" className="iv-btn iv-btn-primary" disabled={loading} onClick={() => loadPart(part)} data-testid="recording-load-btn">
              <Film size={16} /> {loading ? 'Loading recording…' : `Load recording (${(parts.find((x) => x.part === part)?.bytes / 1e6 || 0).toFixed(1)} MB)`}
            </button>
          )}
          {parts.length > 1 && (
            <div className="iv-chips" style={{ marginTop: '0.6rem' }}>
              {parts.map((p) => <button type="button" key={p.part} className={`iv-chip ${p.part === part ? 'is-on' : ''}`} onClick={() => loadPart(p.part)} data-testid={`recording-part-${p.part}`}>Session part {p.part + 1}</button>)}
            </div>
          )}
        </div>
      )}
      <div style={{ maxHeight: 560, overflowY: 'auto' }} data-testid="transcript-list">
        {!turns.length && <p className="iv-small">No transcript yet.</p>}
        {turns.map((t) => {
          const header = t.segment !== lastSeg ? <div key={`seg-${t.seq}`} className="iv-eyebrow" style={{ margin: '0.75rem 0 0.25rem' }}>{SEGMENT_LABELS[t.segment] || t.segment}</div> : null;
          lastSeg = t.segment;
          return <React.Fragment key={t.seq}>{header}<Turn t={t} active={active === t.seq} onClick={() => seekRef.current({ turn: t.seq })} /></React.Fragment>;
        })}
      </div>
    </Section>
  );
}
